import { getErrorMessage } from '../../../constants/common-error-messages'
import {
  ADD_COMMENT_ENDPOINT,
  APPROVE_CASE_ENDPOINT,
  COP_API_BASE_URL,
  getCaseCommentsUrl,
  getCaseDetailsUrl,
  getCaseHistoryUrl,
  REJECT_CASE_ENDPOINT,
} from '../../../constants/service-endpoints'
import { get, postFormData } from '../../../services/http.service'
import { ToastService } from '../../../services/toast.service'

export const DEFAULT_CASE_ID = '500bY00000dohhdQAA'
export type CaseDetailsResponse = Record<string, unknown>

function unwrapODataValue(raw: Record<string, unknown>): Record<string, unknown> {
  const inner = raw.value
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return raw
}

export type CaseHistoryEvent = {
  id?: string | null
  activityType?: string | null
  date?: string | null
  message?: string | null
  user?: string | null
}

export type CaseHistoryResponse = {
  '@odata.context'?: string
  value?: CaseHistoryEvent[]
}

export type CaseAttachment = {
  name?: string | null
  fileExtension?: string | null
}

export type CaseComment = {
  id?: string | null
  date?: string | null
  user?: string | null
  details?: string | null
  attachment?: CaseAttachment | null
}

export type CaseCommentsResponse = {
  '@odata.context'?: string
  value?: CaseComment[]
}

export async function fetchCaseDetails(
  caseId: string = DEFAULT_CASE_ID,
): Promise<CaseDetailsResponse | null> {
  try {
    const url = `${COP_API_BASE_URL}${getCaseDetailsUrl(caseId)}`
    const raw = await get<CaseDetailsResponse>(url)
    if (!raw) return null
    return unwrapODataValue(raw)
  } catch (error) {
    const fallbackMessage = getErrorMessage('case-details-loading')
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export async function fetchCaseComments(
  caseId: string = DEFAULT_CASE_ID,
): Promise<CaseCommentsResponse | null> {
  try {
    const url = `${COP_API_BASE_URL}${getCaseCommentsUrl(caseId)}`
    return await get<CaseCommentsResponse>(url)
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['case-comments-loading'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export async function fetchCaseHistory(
  caseId: string = DEFAULT_CASE_ID,
): Promise<CaseHistoryResponse | null> {
  try {
    const url = `${COP_API_BASE_URL}${getCaseHistoryUrl(caseId)}`
    return await get<CaseHistoryResponse>(url)
  } catch (error) {
    const fallbackMessage = COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
      '{{action}}',
      COP_COMMON_ERROR_MESSAGES['api-error'].action['case-history-loading'],
    )
    const message = error instanceof Error && error.message ? error.message : fallbackMessage
    ToastService.error(message)
    return null
  }
}

export async function approveCaseResolution(caseId: string): Promise<void> {
  const formData = new FormData()
  formData.append('caseId', caseId)
  await postFormData(`${COP_API_BASE_URL}${APPROVE_CASE_ENDPOINT}`, formData)
}

export async function rejectCaseResolution(caseId: string, reason: string): Promise<void> {
  const formData = new FormData()
  formData.append('caseId', caseId)
  formData.append('reason', reason)
  await postFormData(`${COP_API_BASE_URL}${REJECT_CASE_ENDPOINT}`, formData)
}

export async function addCaseComment(
  caseId: string,
  comment: string,
  file?: File,
): Promise<void> {
  const formData = new FormData()
  formData.append('caseId', caseId)
  formData.append('comment', comment)
  if (file) {
    formData.append('file', file)
  }
  const url = `${COP_API_BASE_URL}${ADD_COMMENT_ENDPOINT}`
  await postFormData(url, formData)
}

export default fetchCaseDetails